import { Button, Link, Modal } from "@alpacahq/ui/old";

import type { SubmitAppModalState } from "./SubmitApp.types";

export const OAUTH_DOCS_URL =
  "https://docs.alpaca.markets/docs/using-oauth2-and-trading-api";

export interface SubmitAppModalProps {
  modal: SubmitAppModalState;
  isRegenerating: boolean;
  onClose: () => void;
  onPrimary: () => void;
}

const preserveOneTimeSecret = () => undefined;

const Credentials = ({
  clientId,
  clientSecret,
}: {
  clientId: string;
  clientSecret?: string;
}) => (
  <div className="flex flex-col gap-4 py-4">
    <p>
      <b>Client ID:</b>
      <br />
      <code>{clientId}</code>
    </p>
    {clientSecret && (
      <p>
        <b>Client Secret:</b>
        <br />
        <code>{clientSecret}</code>
      </p>
    )}
  </div>
);

export const SubmitAppModal = ({
  modal,
  isRegenerating,
  onClose,
  onPrimary,
}: SubmitAppModalProps) => {
  if (modal.kind === "closed") {
    return null;
  }

  if (modal.kind === "createSuccess") {
    return (
      <Modal
        controls={
          <Button onClick={onPrimary} type="button">
            Continue
          </Button>
        }
        disableOutsideClick={true}
        isOpen={true}
        onClose={preserveOneTimeSecret}
        title="Your application has been submitted!"
      >
        <p>
          Your credentials are provided below—be sure to store them securely. If
          you lose your client secret, you will need to generate a new one.
        </p>
        <Credentials
          clientId={modal.clientId}
          clientSecret={modal.clientSecret}
        />
        <p>
          Please connect your app to your paper account using your Client ID
          credentials and the information provided{" "}
          <Link href={OAUTH_DOCS_URL}>HERE</Link>.
          <br />
          <br />
          Our team will contact you soon with the next steps.
        </p>
      </Modal>
    );
  }

  if (modal.kind === "editSuccess") {
    return (
      <Modal
        controls={
          <Button onClick={onPrimary} type="button">
            Continue
          </Button>
        }
        isOpen={true}
        onClose={onClose}
        title="App Successfully Updated!"
      >
        <p>You have updated your Alpaca Connect app.</p>
        <Credentials clientId={modal.clientId} />
      </Modal>
    );
  }

  if (modal.kind === "credentials") {
    return (
      <Modal
        controls={
          <div className="flex gap-2">
            <Button
              disabled={isRegenerating}
              onClick={onClose}
              type="button"
              variant="alternate"
            >
              Close
            </Button>
            <Button
              disabled={isRegenerating}
              loading={isRegenerating}
              loadingPlaceholder="Regenerating Secret..."
              onClick={onPrimary}
              type="button"
            >
              Regenerate Secret
            </Button>
          </div>
        }
        isOpen={true}
        onClose={onClose}
        title="Your App Credentials"
      >
        <Credentials clientId={modal.clientId} />
        <p>
          If you need to regenerate your client secret, click below. (Your
          previous client secret will be invalidated.)
        </p>
      </Modal>
    );
  }

  if (modal.kind === "regeneratedSecret") {
    return (
      <Modal
        controls={
          <Button disabled={isRegenerating} onClick={onPrimary} type="button">
            Close
          </Button>
        }
        disableOutsideClick={true}
        isOpen={true}
        onClose={preserveOneTimeSecret}
        title="A new client secret has been generated."
      >
        <p>
          Please store it somewhere only you can see it - it will only be shown
          once.
        </p>
        <Credentials
          clientId={modal.clientId}
          clientSecret={modal.clientSecret}
        />
      </Modal>
    );
  }

  const actionLabel = modal.action === "edit" ? "Updating" : "Creating";
  const resultLabel = modal.action === "edit" ? "updated" : "created";
  return (
    <Modal
      controls={
        <Button onClick={onPrimary} type="button">
          Close
        </Button>
      }
      isOpen={true}
      onClose={onClose}
      title={`Error ${actionLabel} Your App`}
    >
      <p>Your Alpaca Connect app was not {resultLabel}. Please try again.</p>
      <p className="mt-4">
        <b>Error:</b> <code>{modal.message}</code>
      </p>
    </Modal>
  );
};
